import { Component, OnDestroy, Signal, ViewEncapsulation, WritableSignal, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
    CeEmptyStateImports,
    CeListImports,
    CeNotificationListComponent,
    CeScrollService,
    CeTabGroupImports,
    CeTabGroupTab,
    fadeInOnEnterAnimation,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { CeDocumentResizeObserverDirective, CeScrollEventInfo, DocumentResizeEventType } from '@kempower-nx-workspace/shared-directives';
import { EMPTY, filter, map, startWith, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { isEmpty as _isEmpty, last as _last } from 'lodash-es';
import { ContentCdnService, FeedItem } from '@kempower-nx-workspace/chargeye-content';
import { formatDate } from '@kempower-nx-workspace/global-toolkit';
import { ProductUpdateBadgeService } from '../../data-access/product-update-badge.service';
import { CeTemplateHeaderScrollComponent } from '@kempower-nx-workspace/layout-templates';
import { environment } from '@kempower-nx-workspace/ce-environment';

type ReleaseNoteType = 'chargeye' | 'firmware' | 'all';

interface FeedData {
    type: ReleaseNoteType;
    items: FeedItem[];
    allItems: FeedItem[];
}

const typeLabels: Record<string, string> = { chargeye: 'ChargEye update', firmware: 'Firmware update' };

@Component({
    selector: 'ce-app-product-update-menu',
    providers: [CeScrollService, ContentCdnService],
    imports: [
        CommonModule,
        CeTemplateHeaderScrollComponent,
        CeListImports,
        CeEmptyStateImports,
        CeDocumentResizeObserverDirective,
        CeNotificationListComponent,
        CeTabGroupImports,
    ],
    animations: [fadeInOnEnterAnimation],
    templateUrl: './product-update-menu.component.html',
    styleUrl: './product-update-menu.component.scss',
    // None so the scss below can reach the child ce-tab-group's internal
    // .CeTabGroup without ::ng-deep. The single rule is prefixed with the
    // component selector, so it stays effectively scoped to this menu.
    encapsulation: ViewEncapsulation.None,
})
export class ProductUpdateMenuComponent implements OnDestroy {
    readonly #cdnService = inject(ContentCdnService);
    readonly #badgeService = inject(ProductUpdateBadgeService);
    readonly #router = inject(Router);
    readonly #activatedRoute = inject(ActivatedRoute);
    readonly scrollService = inject(CeScrollService);

    readonly scrollSectionId = 'UpdateMenuScroll';

    isScrolled: WritableSignal<boolean> = signal(false);
    isMobile: WritableSignal<boolean | undefined> = signal(undefined);
    hasError: WritableSignal<boolean> = signal(false);

    // Track the entry id in the URL so the highlighted item always matches the
    // route. This menu's ActivatedRoute only carries :productUpdateType (the
    // :productUpdateId is on the sibling content outlet), so derive it from the
    // full router URL on each navigation.
    readonly #currentUrl = toSignal(
        this.#router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(() => this.#router.url),
            startWith(this.#router.url)
        ),
        { initialValue: this.#router.url }
    );
    // Derived (not manually set), so it clears when the URL has no entry — fixes
    // the item staying "selected" after navigating back or switching tabs.
    readonly selectedItemId = computed(() => {
        const data = this.feedData();
        const lastSegment = _last(this.#currentUrl().split('/').filter(Boolean));
        if (!data || !lastSegment || lastSegment === data.type) return '';
        return data.items.some((i) => i.id === lastSegment) ? lastSegment : '';
    });

    feedData = toSignal(
        this.#activatedRoute.paramMap.pipe(
            map((params) => (params.get('productUpdateType') as ReleaseNoteType) ?? 'chargeye'),
            switchMap((type) =>
                this.#cdnService.getFeed(environment.contentCdn?.stage ?? environment.appConfig.envType, { limit: '50' }).pipe(
                    map((res) => ({
                        type,
                        allItems: res.items,
                        items: type === 'all' ? res.items : res.items.filter((item: FeedItem) => item.type === type),
                    } as FeedData)),
                    catchError(() => {
                        this.hasError.set(true);
                        return EMPTY;
                    })
                )
            )
        ),
        { initialValue: null }
    );

    horizontalTabs = computed((): CeTabGroupTab[] => {
        // `path` is required for the mobile (`tabType="page"`) tab group, which renders
        // anchors — without an href they get no pointer cursor, hover or focus styling.
        const data = this.feedData();
        if (!data?.allItems) {
            return [
                { label: 'ChargEye', path: '/help/product-updates/chargeye' },
                { label: 'Firmware', path: '/help/product-updates/firmware' },
                { label: 'All', path: '/help/product-updates/all' },
            ];
        }

        const countUnread = (items: FeedItem[]) =>
            items.filter((item) => this.#badgeService.isUnread(item.publishedAt)).length;

        const ceCount = countUnread(data.allItems.filter((i) => i.type === 'chargeye'));
        const fwCount = countUnread(data.allItems.filter((i) => i.type === 'firmware'));
        const allCount = ceCount + fwCount;
        return [
            { label: 'ChargEye', path: '/help/product-updates/chargeye', ...(ceCount > 0 && { count: ceCount }) },
            { label: 'Firmware', path: '/help/product-updates/firmware', ...(fwCount > 0 && { count: fwCount }) },
            { label: 'All', path: '/help/product-updates/all', ...(allCount > 0 && { count: allCount }) },
        ];
    });

    releaseNoteDataComputed: Signal<{ notificationId: string; isSelected: boolean; title: string; date: string; summary: string; tags: { info: string }[]; notificationLabel: string; unread: boolean }[]> = computed(() => {
        const data = this.feedData();
        const selected = this.selectedItemId();
        if (!data || _isEmpty(data.items)) return [];

        return data.items.map((item: FeedItem) => {
            const tags: { info: string }[] = [];
            if (item.internal) tags.push({ info: 'Internal' });
            return {
                notificationId: item.id,
                isSelected: item.id === selected,
                title: item.title ?? '',
                date: formatDate(item.publishedAt) ?? '',
                summary: item.summary ?? '',
                tags,
                notificationLabel: typeLabels[item.type] ?? item.type,
                unread: this.#badgeService.isUnread(item.publishedAt),
            };
        });
    });


    tabIndexFromRoute = toSignal(
        this.#activatedRoute.paramMap.pipe(
            map((params) => {
                const type = params.get('productUpdateType') as ReleaseNoteType;
                const tabs = this.horizontalTabs();
                const idx = tabs.findIndex((t) => t.label.toLowerCase() === type);
                return idx === -1 ? 0 : idx;
            })
        ),
        { initialValue: null }
    );

    #hasMarkedRead = false;

    syncUrlState = effect(() => {
        const data = this.feedData();
        if (!data || this.isMobile() === undefined) return;

        // Mark as read on first page enter — persists for next session,
        // but keeps the old signal value so pills stay for this session
        if (!this.#hasMarkedRead && data.allItems?.length) {
            this.#hasMarkedRead = true;
            const newest = data.allItems.reduce<string | null>((max, item) => {
                if (!item.publishedAt) return max;
                return max && item.publishedAt <= max ? max : item.publishedAt;
            }, null);
            if (newest) this.#badgeService.persistForNextSession(newest);
        }

        const urlParts = this.#router.url.split('/').filter(Boolean);
        const lastSegment = _last(urlParts);
        const hasDetail = lastSegment !== data.type;
        const isDesktop = this.isMobile() === false;

        // Pane visibility is derived from the detail outlet's activation state by
        // the wide split-view layout itself — nothing to push here. This effect
        // only auto-selects the first entry on desktop and scrolls the list.
        if (hasDetail && lastSegment) {
            const item = data.items.find((i) => i.id === lastSegment);
            if (item) {
                queueMicrotask(() => document.getElementById(item.id)?.scrollIntoView({ block: 'nearest' }));
            }
        } else if (isDesktop && !_isEmpty(data.items)) {
            const firstItem = data.items[0];
            this.#router.navigate([`/help/product-updates/${data.type}/${firstItem.id}`]);
        }
    });


    itemResizeEvent($event: DocumentResizeEventType): void {
        this.isMobile.set($event.contentWidth <= 719);
    }

    onElementScroll($event: CeScrollEventInfo): void {
        if ($event) this.isScrolled.set($event.elementScrollTop > 0);
    }

    notificationIdSelected(params: { id: string; unread: boolean }): void {
        const data = this.feedData();
        if (!data?.type) return;
        // selectedItemId is derived from the URL; navigating updates it.
        this.#router.navigate([`/help/product-updates/${data.type}/${params.id}`]);
    }

    getLabelByIndex(index: number): ReleaseNoteType {
        return this.horizontalTabs()[index].label.toLowerCase() as ReleaseNoteType;
    }

    onTabSelection(selectedTabIndex: number): void {
        const tabName = this.getLabelByIndex(selectedTabIndex);
        this.#router.navigate([`/help/product-updates/${tabName}`]);
    }

    ngOnDestroy(): void {
        this.#badgeService.markAllRead();
    }
}
